import type { OperationOrigin, PageRoutine, RoutineFailure } from './routine'
import { RoutineError, routineFailureReason } from './routine'

export type UiOperation = { type: string } & Record<string, unknown>

export type UiOperationSource = { id: string; label?: string }

export type PendingUiOperation = {
  operation: UiOperation
  source?: UiOperationSource
}

export type UiOperationSuccess = { status: 'success'; value?: unknown }
export type UiOperationFailure = { status: 'error'; reason: RoutineFailure; message: string; code?: string }
export type UiOperationResult = UiOperationSuccess | UiOperationFailure

export type OperationHandler = {
  ready: boolean
  handle: (operation: UiOperation) => Promise<unknown> | unknown
}

export type PageLoadState = 'loading' | 'ready' | 'failed'

const SELF_SETTLED = Symbol('uiActions.selfSettled')

type SelfSettled = { [SELF_SETTLED]: true; value: unknown }

/**
 * Marks an operation the page has finished with, so the runner approves the tool call at once instead
 * of holding it for the user's Save. Use it for anything that is done the moment it returns: opening a
 * page, sending a chat message, or a delete the user already confirmed. Only creates and updates, which
 * leave a draft in the form, should come back bare and wait for Save or Approve.
 */
export function selfSettled<T>(value?: T): SelfSettled {
  return { [SELF_SETTLED]: true, value }
}

/**
 * Runs the page's delete, which asks the user to confirm, and settles the tool call itself. The page
 * usually navigates away once the delete succeeds, so a call left for Approve would never settle.
 */
export async function requestDeleteOperation(
  routine: PageRoutine,
  unsupportedMessage: string,
  origin: OperationOrigin = 'assistant'
): Promise<SelfSettled> {
  if (!routine.requestDelete) throw unsupported(unsupportedMessage)
  return selfSettled(await routine.requestDelete(origin))
}

export function isSelfSettled(value: unknown): boolean {
  return typeof value === 'object' && value !== null && (value as Partial<SelfSettled>)[SELF_SETTLED] === true
}

export function unwrapOperationValue(value: unknown): unknown {
  return isSelfSettled(value) ? (value as SelfSettled).value : value
}

export const PAGE_OPERATION_TYPES = ['ui_save', 'ui_discard'] as const

export type PageOperationType = (typeof PAGE_OPERATION_TYPES)[number]

export function isPageOperation(type: string): type is PageOperationType {
  return (PAGE_OPERATION_TYPES as readonly string[]).includes(type)
}

export async function runPageOperation(type: PageOperationType, routine: PageRoutine): Promise<unknown> {
  switch (type) {
    case 'ui_save':
      if (!routine.save) throw unsupported('This page cannot be saved')
      // Only the Workforce Assistant asks for ui_save, so the save is attributed to it.
      return routine.save('assistant')
    case 'ui_discard':
      if (!routine.discard) throw unsupported('This page has nothing to discard')
      return routine.discard()
  }
}

export function successResult(value: unknown): UiOperationSuccess {
  return value === undefined ? { status: 'success' } : { status: 'success', value }
}

export function errorResult(error: unknown): UiOperationFailure {
  const result: UiOperationFailure = {
    status: 'error',
    reason: routineFailureReason(error),
    message: error instanceof Error ? error.message : String(error),
  }
  if (error instanceof RoutineError && error.code) result.code = error.code
  return result
}

export function unsupported(message: string): RoutineError {
  return new RoutineError('unsupported', message)
}
